import { getTemplateVariant } from './templateVariants.js'
import { withoutCssComments } from './bannerFills.js'
import builtins from './templates.json'
import nativeColorVariables from './nativeColorVariables.json'
import { jerseyDataUri, normalizeSportKey } from './jerseySvg.js'
import teamMarks from '../teammarks/teamMarks.json'

const TEMPLATE_ASSET_URL_PATTERN = /url\(\s*(['"]?)\/prepacks\/([\w.-]+)\1\s*\)/g
const WORD_INITIAL_PATTERN = /\b\w/g
const WHITESPACE_OR_UNDERSCORE_RUN_PATTERN = /[\s_]+/g
const HTTPS_AUTHORITY_PATTERN = /^https:\/\/([^/?#]*)/i
const WORD_SEPARATOR_PATTERN = /[^\p{L}\p{N}]+/u
const OUTCOME_TYPE_PREFIX_PATTERN = /^ot_/
const LEADING_PUNCTUATION_PATTERN = /^\s*[-–—:]\s*/
const PLAYER_WORD_PREFIX_PATTERN = /^player\s+(?=\S)/i
const REGEXP_META_CHARACTERS_PATTERN = /[.*+?^${}()|[\]\\]/g
const WHITESPACE_RUN_PATTERN = /\s+/g
const TRAILING_SELECTION_LINE_PATTERN = /\s([+-]?\d+(?:\.\d+)?)$/
const TOTAL_SELECTION_PREFIX_PATTERN = /^(?:over|under)\s+[+-]?\d/i
const DIGIT_PATTERN = /\d/
const HTML_ESCAPE_CHARACTER_PATTERN = /[&<>"']/g
const EMPTY_SUBTITLE_ELEMENT_PATTERN = /<([a-z][\w-]*)\b[^>]*>\s*\{\{SUBTITLE\}\}\s*<\/\1>/gi
const COPIES_COUNTER_ELEMENT_PATTERN = /<([a-z][\w-]*)\b[^>]*>[^<]*\{\{COPIES\}\}[^<]*<\/\1>/gi
const DECORATIVE_MARKUP = String.raw`(?:\s|<svg\b[\s\S]*?<\/svg>|<(?:img|br|i|use)\b[^>]*\/?>)*`
const COPIES_WRAPPER_PATTERN = new RegExp(String.raw`<([a-z][\w-]*)\b[^>]*>${DECORATIVE_MARKUP}<!--copies-->${DECORATIVE_MARKUP}<\/\1>`, 'gi')
const COPIES_COMMENT_PATTERN = /<!--copies-->/g
const TEMPLATE_TOKEN_PATTERN = /\{\{([A-Z_0-9]+)\}\}/g
const CSS_UNSAFE_CHARACTER_PATTERN = /[;{}<>]|\/\*|\\/
const COMBINED_ODDS_TOKENS_PATTERN = /\{\{COMBINED_ODDS\}\}/g
const HTML_TAG_OR_TEXT_PATTERN = /<\/?([a-z][\w:-]*)\b[^>]*>|[^<]+/gi
const COMBINED_ODDS_TOKEN_PATTERN = /\{\{COMBINED_ODDS\}\}/
const DEFAULT_BET_BUTTON_LABEL_PATTERN = /^(?:Edit|View details|Tail it!|Lock it in|Add to slip)$/
const HTML_CLASS_ATTRIBUTE_PATTERN = /\bclass\s*=\s*["']([^"']*)["']/i
const WHITESPACE_PATTERN = /\s+/
const BUTTON_ROLE_ATTRIBUTE_PATTERN = /\brole\s*=\s*["']button["']/i
const HTML_ANCHOR_PATTERN = /<a(?=\s|>)(?:[^>"']|"[^"]*"|'[^']*')*>/gi
const HTML_HREF_ATTRIBUTE_PATTERN = /\s+href\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi
const BET_BOOST_LABEL_PATTERN = /Bet boost(?: »)?/g
const EMPTY_TEMPLATE_FOOTER_PATTERN = /<div class=["'](?:footer|options)["']>\s*<\/div>/gi

const builtinById = new Map(builtins.map((template) => [template.id, template]))
const canonicalPublicArtwork = new WeakMap()

function hasCanonicalArtwork(template, builtin) {
  if (!builtin || !template) return false
  if (template.htmlCode === builtin.htmlCode) return true
  const prepared = canonicalPublicArtwork.get(template)
  return prepared?.id === builtin.id && prepared.htmlCode === template.htmlCode
}

function publicArtwork(template, htmlCode) {
  const prepared = { ...template, htmlCode }
  if (hasCanonicalArtwork(template, builtinById.get(template.id))) {
    canonicalPublicArtwork.set(prepared, { id: template.id, htmlCode })
  }
  return prepared
}

// Builtin artwork references assets as /prepacks/<file>; the bundler-resolved
// URLs are substituted at render time so the same JSON works in Studio and the widget.
const BUILTIN_ASSET_URLS = {
  'boosted-header.png': new URL('./assets/boosted-header.png', import.meta.url).href,
  'oswald-700.woff2': new URL('./assets/oswald-700.woff2', import.meta.url).href,
  'lilita-one-400.woff2': new URL('./assets/lilita-one-400.woff2', import.meta.url).href,
}
export const LEG_LAYOUT_CSS = '.legs{display:flex;flex-direction:column;gap:8px}.leg{display:flex;align-items:center;justify-content:space-between;gap:8px}.leg-copy{display:flex;flex-direction:column;gap:2px;min-width:0}.leg-odds{flex-shrink:0}'

export function resolveBuiltinCssUrls(css) {
  return css.replace(
    TEMPLATE_ASSET_URL_PATTERN,
    (match, _quote, file) => (BUILTIN_ASSET_URLS[file] ? `url("${BUILTIN_ASSET_URLS[file]}")` : match),
  )
}

const PACK_TYPE_LABELS = {
  accumulator: 'Accumulator',
  home_run_props: 'Home Run Props',
  moneyline_parlay: 'Moneyline Parlay',
  parlay: 'Parlay',
  player_props: 'Player Props',
  same_game_parlay: 'SGP',
  sgp_plus: 'SGP+',
  straight: 'Straight',
  team_totals: 'Team Totals',
  touchdown_scorers: 'Touchdown Scorers',
}
const DECIMAL_PACK_TYPE_LABELS = {
  parlay: 'Multiple',
  same_game_parlay: 'Bet Builder',
  sgp_plus: 'Bet Builder +',
  moneyline_parlay: 'Match Winner Multiple',
  straight: 'Single',
}

export function packTypeLabel(value, oddsFormat) {
  if (!value) return oddsFormat === 'decimal' ? 'Multiple' : 'Parlay'
  const labels = oddsFormat === 'decimal' ? { ...PACK_TYPE_LABELS, ...DECIMAL_PACK_TYPE_LABELS } : PACK_TYPE_LABELS
  return labels[value] || String(value)
    .replaceAll('_', ' ')
    .replace(WORD_INITIAL_PATTERN, (character) => character.toUpperCase())
}

// The card's bet type badge names the wager in sportsbook shorthand (#1031): SGP, as the feed cards and the
// builder's Bet Structure cards say it, never "Same-Game Parlay", which is the strategy's name.
export const BET_TYPE_LABELS = {
  parlay: 'Parlay',
  same_game_parlay: 'SGP',
  sgp_plus: 'SGP+',
}
const DECIMAL_BET_TYPE_LABELS = {
  parlay: 'Multiple',
  same_game_parlay: 'Bet Builder',
  sgp_plus: 'Bet Builder +',
}

export function betTypeLabel(value, oddsFormat) {
  if (!value) return ''
  const labels = oddsFormat === 'decimal' ? { ...BET_TYPE_LABELS, ...DECIMAL_BET_TYPE_LABELS } : BET_TYPE_LABELS
  return labels[value] || packTypeLabel(value, oddsFormat)
}


// Native cards use branding tokens rather than artwork CSS variables.
// Descriptors define the bounded variables that can be overridden. The list is
// shared with the Studio API (native_colors.go), which restyles these colors.
export const NATIVE_COLOR_VARIABLES = nativeColorVariables

// How a pack's card draws its tags (#1037): quiet outlined chips unless the
// operator chose filled, the bet-type chip's fill. A template_vars key the
// Studio API validates (prepacks.TagStyleVariable); quiet is stored as no key.
export const TAG_STYLE_VARIABLE = '--tag-style'
export const TAG_STYLE_QUIET = 'quiet'
export const TAG_STYLE_FILLED = 'filled'

export function deriveBetType(legs = []) {
  if (!Array.isArray(legs) || legs.length < 2) return ''

  const eventCounts = new Map()
  const outcomeIds = new Set()
  for (const leg of legs) {
    const outcomeId = String(leg?.outcome_id ?? leg?.outcomeId ?? '').trim()
    const eventId = String(leg?.event_id ?? leg?.eventId ?? '').trim()
    if (!outcomeId || !eventId || outcomeIds.has(outcomeId)) return ''
    outcomeIds.add(outcomeId)
    eventCounts.set(eventId, (eventCounts.get(eventId) || 0) + 1)
  }

  let sameGame = false
  for (const count of eventCounts.values()) {
    if (count > 1) {
      sameGame = true
      break
    }
  }
  if (!sameGame) return 'parlay'
  return eventCounts.size === 1 ? 'same_game_parlay' : 'sgp_plus'
}


// Builder sport ids (`motorsports`, `mma`, `rugby`) and the Kambi sport ids legs from the leg search carry
// (`rugby_union`, `martial_arts`, `formula_1`) draw the same glyph.
const SPORT_GLYPHS = {
  soccer: '\u26bd', football: '\u26bd', american_football: '\ud83c\udfc8', basketball: '\ud83c\udfc0',
  baseball: '\u26be', hockey: '\ud83c\udfd2', ice_hockey: '\ud83c\udfd2', tennis: '\ud83c\udfbe', golf: '\u26f3',
  mma: '\ud83e\udd4a', martial_arts: '\ud83e\udd4a', boxing: '\ud83e\udd4a',
  rugby: '\ud83c\udfc9', rugby_union: '\ud83c\udfc9', rugby_league: '\ud83c\udfc9', cricket: '\ud83c\udfcf', volleyball: '\ud83c\udfd0',
  motorsport: '\ud83c\udfc1', motorsports: '\ud83c\udfc1', formula_1: '\ud83c\udfc1', nascar: '\ud83c\udfc1', indy_car: '\ud83c\udfc1',
  motorcycling: '\ud83c\udfc1', esports: '\ud83c\udfae',
}

// Leagues whose "football" is American football: legs from before Kambi's sport names say "football" for the
// NFL, college football ("NCAAF Top 25") and the CFL, with the league beside it. The Go catalog's
// NormalizeLeague reads the same names.
const AMERICAN_FOOTBALL_LEAGUES = new Set(['NFL', 'CFL', 'CFB', 'AMERICAN FOOTBALL'])
const AMERICAN_FOOTBALL_LEAGUE_PREFIXES = ['NCAAF', 'NCAA FOOTBALL', 'COLLEGE FOOTBALL']

function isAmericanFootballLeague(league) {
  const name = String(league ?? '').trim().toUpperCase().replace(WHITESPACE_OR_UNDERSCORE_RUN_PATTERN, ' ')
  return AMERICAN_FOOTBALL_LEAGUES.has(name) || AMERICAN_FOOTBALL_LEAGUE_PREFIXES.some((prefix) => name.startsWith(prefix))
}

/**
 * Emoji for a sport key; unknown sports get a neutral trophy. "football" is soccer, as Kambi names it, unless
 * `league` is an American football one (NFL, CFL, college football), which older legs pair it with.
 */
export function sportGlyph(sport, league = '') {
  const key = normalizeSportKey(sport)
  if (key === 'football' && isAmericanFootballLeague(league)) return SPORT_GLYPHS.american_football
  return SPORT_GLYPHS[key] || '\ud83c\udfc6'
}

// Hosts a leg's team logo may load from (#572): the public Shape Games icon
// service's asset host. The server stores a logo only for organizations allowed
// to show them; the renderer still drops any other URL. Shared with Go
// (package teammarks), which stores only these.
export const TEAM_LOGO_HOSTS = Object.freeze([...teamMarks.logo_hosts])

const MAX_LOGO_URL_LENGTH = 512

/**
 * The logo URL if it is https on an allowed host, else ''. Mirrors Go's
 * models.IsAllowedPrepackLogoURL: at most 512 characters, no user info, no
 * explicit port (not even :443, which URL would normalise away), no fragment.
 */
export function teamLogoUrl(value) {
  if (typeof value !== 'string') return ''
  const raw = value.trim()
  if (!raw || raw.length > MAX_LOGO_URL_LENGTH || raw.includes('#')) return ''
  // The authority exactly as written: a port, user info or a trailing colon makes it differ from the host.
  const authority = HTTPS_AUTHORITY_PATTERN.exec(raw)?.[1]?.toLowerCase()
  if (!authority || !TEAM_LOGO_HOSTS.includes(authority)) return ''
  let url
  try {
    url = new URL(raw)
  } catch {
    return ''
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash || !TEAM_LOGO_HOSTS.includes(url.hostname)) return ''
  return url.href
}

const TEAM_ABBR = /^[A-Z0-9]{2,4}$/

/**
 * LOGO, JERSEY, JERSEY_PLAIN, TEAM and MARK for one leg; MARK is what the leg
 * can show with its best data: logo, jersey or glyph. JERSEY_PLAIN is the same
 * jersey without the shirt number, for templates that hide numbers (#602).
 */
function legTeamTokens(leg, sport) {
  if (!leg) return { logo: '', jersey: '', plainJersey: '', team: '', mark: '' }
  const team = leg.team && typeof leg.team === 'object' ? leg.team : null
  const logo = teamLogoUrl(team?.logo_url)
  const legSport = leg.sport || sport
  const jersey = team ? jerseyDataUri(team, legSport) : ''
  // Most teams carry no number, and then both jerseys are the same drawing.
  const plainJersey = team && team.number ? jerseyDataUri({ ...team, number: undefined }, legSport) : jersey
  return {
    logo,
    jersey,
    plainJersey,
    team: typeof team?.abbr === 'string' && TEAM_ABBR.test(team.abbr) ? team.abbr : '',
    mark: logo ? 'logo' : jersey ? 'jersey' : 'glyph',
  }
}

// The srcdoc token injector runs in shared plain JavaScript, so it cannot import
// the widget's TypeScript utility. Keep this copy's options and rounding in
// lockstep with apps/feed-widget/src/utils/money.ts.
export function formatMoney(amount, currency = 'USD', locale, { wholeWithoutCents = false } = {}) {
  const roundedAmount = Math.round(amount * 100) / 100
  const options = { style: 'currency', currency }
  if (wholeWithoutCents && Number.isInteger(roundedAmount)) options.minimumFractionDigits = 0
  try {
    return new Intl.NumberFormat(locale, options).format(roundedAmount)
  } catch {
    return new Intl.NumberFormat(undefined, { ...options, currency: 'USD' }).format(roundedAmount)
  }
}

function americanToMultiplier(american) {
  const value = Number(String(american ?? '').replace('+', ''))
  if (!Number.isFinite(value) || value === 0) return NaN
  return value > 0 ? 1 + value / 100 : 1 + 100 / Math.abs(value)
}

// This odds copy is likewise needed by plain-JS srcdoc token injection. The
// American path deliberately never synthesizes a price from a payout multiplier.
export function displayOdds(american, decimal, oddsFormat) {
  if (oddsFormat === 'decimal') {
    const decimalValue = Number(decimal)
    const multiplier = decimalValue > 1 ? decimalValue : americanToMultiplier(american)
    return Number.isFinite(multiplier) ? multiplier.toFixed(2) : '--'
  }
  return american == null || american === '' ? '' : String(american)
}

export function examplePayoutLabel(pack, stake = 10, { currency = 'USD', locale = 'en-US' } = {}) {
  let multiplier = Number(pack?.payout_multiplier ?? pack?.payoutMultiplier)
  if (!Number.isFinite(multiplier) || multiplier <= 1) multiplier = americanToMultiplier(pack?.odds ?? pack?.combinedOdds)
  if (!Number.isFinite(multiplier)) return ''
  return `${formatMoney(stake, currency, locale, { wholeWithoutCents: true })} pays ${formatMoney(stake * multiplier, currency, locale, { wholeWithoutCents: true })}`
}

// A side ("Over 6.5", "Yes", "Draw") names neither a team nor a player, so a
// leg backing one reads only with its matchup. Roles are the engine's
// (sportsSearchOutcomeRole, "ot_" stripped) plus the three-way draw/tie.
const SIDE_ROLES = new Set(['over', 'under', 'yes', 'no', 'draw', 'tie'])
const SIDE_SELECTION = /^(?:over|under|yes|no|draw|tie)\b/i
const LINE_SELECTION = /^(?:over|under)\b/i
const GAME_LINE_FAMILIES = new Set(['game_total', 'team_total'])
// The same matchup separators as prepackSurfaceModel: "Away @ Home", "Home vs Away", "Home - Away".
const MATCHUP_SEPARATOR = /\s+(?:vs\.?|v\.?|@|at|-)\s+/i

function legText(leg, ...keys) {
  for (const key of keys) {
    const value = leg?.[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

// True when a word of either side of the matchup ("Rays" of "TB Rays") appears in the selection.
function namesMatchupSide(selection, eventLabel) {
  const words = new Set(selection.toLowerCase().split(WORD_SEPARATOR_PATTERN).filter(Boolean))
  return eventLabel.split(MATCHUP_SEPARATOR).some((side) => side.toLowerCase().split(WORD_SEPARATOR_PATTERN).some((word) => word.length > 2 && words.has(word)))
}

/**
 * Whether a leg needs its matchup printed to be readable: it names no
 * participant or player, and its selection is a side ("Over 6.5", "Yes",
 * "Draw") or a game/team total line that names no team. Moneylines and
 * spreads name their team ("New York Yankees", "Yankees -1.5") and do not.
 * Reads snake_case and camelCase legs.
 */
export function legNeedsEvent(leg) {
  if (!leg || legText(leg, 'participant', 'player')) return false
  const selection = legText(leg, 'selection_name', 'selectionName')
  // A selection that names a side of the matchup ("Packers Over 24.5" on a
  // team total) reads on its own, whatever its role.
  if (namesMatchupSide(selection, legText(leg, 'event_label', 'eventLabel'))) return false
  const role = legText(leg, 'outcome_role', 'outcomeRole').toLowerCase().replace(OUTCOME_TYPE_PREFIX_PATTERN, '')
  if (SIDE_ROLES.has(role) || SIDE_SELECTION.test(selection)) return true
  const families = [legText(leg, 'market_family', 'marketFamily'), legText(leg, 'market_type', 'marketType')].map((value) => value.toLowerCase())
  return families.some((family) => GAME_LINE_FAMILIES.has(family))
}

// Stat categories as a bet line names them when the market label names no
// unit of its own; any other category is its words title-cased
// ("passing_yards" → "Passing Yards").
const STAT_LABELS = { rbis: 'RBIs', hits_runs_rbis: 'Hits + Runs + RBIs', blocks_steals: 'Blocks + Steals', threes: '3-Pointers', interceptions_defensive: 'Interceptions', multi_goals: 'Goals' }
function statLabel(category) {
  const key = String(category || '').trim().toLowerCase()
  if (!key) return ''
  return STAT_LABELS[key] || key.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

// The catalog's periods (sportsmarket/catalog.json) as a bet line names them
// when the market label carries no period phrase of its own.
const PERIOD_LABELS = { half_1: '1st Half', half_2: '2nd Half', quarter_1: '1st Quarter', quarter_2: '2nd Quarter', quarter_3: '3rd Quarter', quarter_4: '4th Quarter', period_1: '1st Period', period_2: '2nd Period', period_3: '3rd Period', first_5_innings: '1st 5 Innings', inning_1: '1st Inning' }

// Search heads a player's markets "<Name> - Player <Stat>"; Kambi suffixes
// settlement notes ("- Including Overtime", "(Listed player …)", "(Settled
// using Opta data)") and attributions ("Total Passing Yards by the Player",
// "Player's shots on target"). None of it belongs under a row that already
// names the player.
const PLAYER_HEADING = /^.+?\s+[-–—]\s+(?=player\b)/i
const LABEL_NOISE = /\s*(?:[-–—]\s*including\b.*|\((?:listed player|this includes|including|settled)[^)]*\))\s*$/i
// The attribution alone, wherever it sits, so a period after it stays:
// "Total Hits by the Player - 3rd Inning" keeps its inning.
const PLAYER_ATTRIBUTION = /\s+(?:(?:thrown|scored|made|recorded|hit|allowed|pitched|won)\s+)?by the player\b/gi
const PLAYER_POSSESSIVE = /^player['’]s\s+(?=\S)/i
const PERIOD_SEPARATOR = /\s+[-–—]\s+/

// The market as the row's bet line reads it: its head ("Points", "Point
// Spread") and the period phrase the book wrote after a dash ("1st Half"),
// from `bet_label` (the Search bet's own criterion, when Studio stored it)
// else `market_label`, without the "<Name> - Player" heading, a leading
// "Player" and Kambi's notes. A leg off the full game whose label carries no
// phrase names its period from `period`.
function marketParts(leg, participant) {
  let base = legText(leg, 'bet_label', 'betLabel') || legText(leg, 'market_label', 'marketLabel', 'market_name')
  // A note may follow the period phrase ("… - 1st Half (Settled using Opta data)"), so strip notes until none is left.
  for (let previous = ''; previous !== base; ) { previous = base; base = base.replace(LABEL_NOISE, '') }
  base = base.replace(PLAYER_ATTRIBUTION, '').replace(PLAYER_HEADING, '')
  if (participant && base.toLowerCase().startsWith(participant.toLowerCase())) {
    base = base.slice(participant.length).replace(LEADING_PUNCTUATION_PATTERN, '')
  }
  base = base.replace(PLAYER_POSSESSIVE, '').replace(PLAYER_WORD_PREFIX_PATTERN, '').trim()
  const [first = '', ...rest] = base.split(PERIOD_SEPARATOR)
  const period = legText(leg, 'period').toLowerCase()
  const phrase = rest.join(' - ') || (period && period !== 'full_game' ? PERIOD_LABELS[period] || '' : '')
  return { head: first ? first.charAt(0).toUpperCase() + first.slice(1) : '', phrase }
}

export const escapeRegExp = (text) => text.replace(REGEXP_META_CHARACTERS_PATTERN, '\\$&')
// "Yes 1" of "Bruno Fernandes Yes 1", "+0.5" of "DC United +0.5", "" of a
// moneyline; "-3.5" of "Kansas City Chiefs -3.5" under the participant "Chiefs".
function selectionSide(selection, participant) {
  if (!participant) return selection
  if (selection.toLowerCase().includes(participant.toLowerCase())) {
    return selection.replace(new RegExp(escapeRegExp(participant), 'i'), ' ').replace(WHITESPACE_RUN_PATTERN, ' ').trim()
  }
  const trailing = selection.match(TRAILING_SELECTION_LINE_PATTERN)
  if (trailing && participant.toLowerCase().includes(selection.slice(0, trailing.index).trim().toLowerCase())) return trailing[1]
  return selection
}

// The family and market keys that name a handicap, the set selection_line.go
// keeps as spreadMarketKeys; Studio's legModel.js imports these two.
export const SPREAD_KEYS = new Set(['team_spread', 'spread', 'set_spread'])
/** A handicap leg, full game or period (a 1st-half spread's period key is "spread"). */
export const isSpreadLeg = (leg) => ['market_family', 'market_type', 'canonical_market_key', 'period_market_key'].some((key) => SPREAD_KEYS.has(legText(leg, key).toLowerCase()))
// The book's own scorer wording, kept for a 1+ threshold ("Anytime Touchdown Scorer").
const SCORER_WORDS = /\b(?:scorer|to score|to hit|to record|anytime)\b/i
// A market that names its count ("2+ Touchdowns", "To score at least 2 goals").
const COUNT_WORDS = /\d+\s*\+|\bat least \d|\b\d+ or more\b/i
// A bet the book wrote as a verb phrase ("To give an assist", "To record a
// double-double"): the phrase is the bet, whatever its line.
const VERB_PHRASE = /^to\s+\w/i
const SIDE_LINE = /^(over|under|yes|no)?\s*([+-]?\d+(?:\.\d+)?)?$/i
const plainLine = (value) => (value === 0 ? '0' : String(value))
const signedLine = (value) => (value > 0 ? `+${value}` : plainLine(value))

/**
 * What a leg bets, for the row under its player or team (#865): the market
 * and the side, never the name again. "1+ Goals", "25+ Points", "Over 249.5
 * Passing Yards", "Spread +0.5", "Full Time", and the period after the bet
 * when the leg is off the full game ("Over 12.5 Points - 1st Half"). A "No"
 * is the same bet declined ("1+ Goals: No"). `bet_label` (the sportsbook's
 * own words for the bet, when Studio stored them) stands in for the market.
 * An explicit `bet` (sample packs) is printed as is, and a leg without a
 * participant keeps its market: its selection is the row. Reads snake_case
 * and camelCase legs.
 */
export function legBetLabel(leg, oddsFormat) {
  const explicit = legText(leg, 'bet')
  if (explicit) return explicit
  const participant = legText(leg, 'participant', 'player')
  const selection = legText(leg, 'selection_name', 'selectionName')
  const market = legText(leg, 'market_label', 'marketLabel', 'market_name')
  if (!participant) return market
  const { head, phrase } = marketParts(leg, participant)
  const withPeriod = (text) => (phrase && text && !text.toLowerCase().includes(phrase.toLowerCase()) ? `${text} - ${phrase}` : text)
  const side = selectionSide(selection, participant)
  const parsed = side.match(SIDE_LINE)
  if (!parsed && TOTAL_SELECTION_PREFIX_PATTERN.test(side)) return withPeriod(side)
  const role = legText(leg, 'outcome_role', 'outcomeRole').toLowerCase().replace(OUTCOME_TYPE_PREFIX_PATTERN, '')
  const family = legText(leg, 'market_family', 'marketFamily').toLowerCase()
  const unit = !head || SCORER_WORDS.test(head) ? statLabel(legText(leg, 'stat_category', 'statCategory')) || head : head
  const direction = role === 'over' || role === 'under' ? role : (parsed?.[1] || '').toLowerCase()
  const stored = leg?.line === null || leg?.line === undefined || leg?.line === '' ? NaN : Number(leg.line)
  const line = Number.isFinite(stored) ? stored : (parsed?.[2] !== undefined ? Number(parsed[2]) : NaN)
  if (direction === 'over' || direction === 'under') {
    const word = direction === 'over' ? 'Over' : 'Under'
    return withPeriod((Number.isFinite(line) ? `${word} ${plainLine(line)} ${unit}` : `${word} ${unit}`).trim())
  }
  if (isSpreadLeg(leg) && Number.isFinite(line)) return withPeriod(`${head || 'Spread'} ${signedLine(line)}`)
  const negative = role === 'no' || direction === 'no'
  if (negative || role === 'yes' || direction === 'yes' || family === 'player_score' || family === 'player_milestone') {
    const positive = VERB_PHRASE.test(head) || COUNT_WORDS.test(head) || (SCORER_WORDS.test(head) && !(line > 1))
      ? head
      : (Number.isFinite(line) && line > 0 && unit ? `${Math.ceil(line)}+ ${unit}` : head)
    if (negative) return withPeriod(positive ? `${positive}: No` : 'No')
    return withPeriod(positive || side || selection)
  }
  if (!head && family === 'team_moneyline') return withPeriod(oddsFormat === 'decimal' ? 'Match Winner' : 'Moneyline')
  if (!parsed && DIGIT_PATTERN.test(side) && head && !head.toLowerCase().includes(side.toLowerCase())) return withPeriod(`${head}: ${side}`)
  return withPeriod(head || side || selection)
}

export function packGames(legs) {
  const games = new Map()
  for (const leg of Array.isArray(legs) ? legs : []) {
    const label = legText(leg, 'event_label', 'eventLabel')
    const key = String(leg?.event_id ?? leg?.eventId ?? '').trim() || label
    if (key && !games.has(key)) games.set(key, label)
  }
  return games
}

/**
 * What a pack names as its game: "3 games" when its legs span several, else
 * `matchup`, the caller's single-game label. A pack across games has no one
 * matchup, so a caller's value (often the first leg's game) never wins there.
 */
export function packEventLabel(legs, matchup = '') {
  const count = packGames(legs).size
  return count > 1 ? `${count} games` : matchup
}

// The bet slot of a leg whose matchup took the player slot. A line reads as
// one phrase, selection first ("Over 6.5 Total Runs", like "Over 249.5 Pass
// Yds"); any other side follows its market ("Both Teams To Score: Yes").
function sideBetLabel(selection, market) {
  if (!selection || !market) return selection || market
  const lowerSelection = selection.toLowerCase()
  const lowerMarket = market.toLowerCase()
  if (lowerMarket.includes(lowerSelection)) return market
  if (lowerSelection.includes(lowerMarket)) return selection
  return LINE_SELECTION.test(selection) ? `${selection} ${market}` : `${market}: ${selection}`
}

export function escapeHtml(value) {
  return String(value ?? '').replace(HTML_ESCAPE_CHARACTER_PATTERN, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[character])
}

// `visibleLegCount` caps how many legs the artwork lists; legs past it inject
// as empty so every template's `.leg:has(strong:empty){display:none}` drops
// the row and the card keeps a bounded height. LEG_COUNT still names the
// whole pack, and the native shell offers "See all N bets" for the rest.
//
// Per leg, LEG_n_PLAYER is the participant (else the selection) and
// LEG_n_BET what the leg bets (`legBetLabel`, #865): under a participant
// the market and the side ("1+ Goals", "Over 249.5 Passing Yards", "Spread
// +0.5", "Full Time"), never the name again; else an explicit `bet`, else
// the market. A leg whose selection is only a side (`legNeedsEvent`: a game
// total's "Over 6.5") would read "Over 6.5" over "Total Runs" with no game,
// so when it has a matchup the matchup takes the player slot and the side
// and market the bet slot ("TB Rays @ PIT Pirates" over "Over 6.5 Total
// Runs"), and its LEG_n_EVENT is empty so a template that also prints the
// game does not print it twice.
// LEG_n_FORM is client-supplied recent form ("L10 7/10"), empty until an
// opted-in widget receives a valid series. Pack/server fields never fill it.
export function injectPackTokens(html, pack, { exampleStake = 10, visibleLegCount = Infinity, oddsFormat, currency = 'USD', locale, legForms = [] } = {}) {
  if (!html) return ''
  if (!pack) return html
  let markup = html
  const legs = Array.isArray(pack.legs) ? pack.legs : []
  const firstLeg = legs[0] || {}
  const hasOwn = (key) => Object.prototype.hasOwnProperty.call(pack, key)
  const derived = deriveBetType(legs)
  const activeBetType = hasOwn('bet_type') ? pack.bet_type : derived
  // Templates label cards by wager structure; fall back to the editorial pack type so the tag is never blank.
  const type = (activeBetType ? betTypeLabel(activeBetType, oddsFormat) : '') || packTypeLabel(pack.pack_type || pack.type, oddsFormat)
  const combinedOdds = hasOwn('odds') ? (pack.odds ?? '') : (pack.combinedOdds ?? '')
  const event = packEventLabel(legs, pack.event || firstLeg.event_label || firstLeg.eventLabel || '')
  const tags = Array.isArray(pack.tags) ? pack.tags : []
  const subtitle = typeof pack.subtitle === 'string' ? pack.subtitle.trim() : ''
  // Drop an empty tagline's element so it leaves no gap.
  if (!subtitle) {
    markup = markup.replace(EMPTY_SUBTITLE_ELEMENT_PATTERN, '')
  }
  const copies = Math.floor(Number(pack.copies))
  // Counters read as real data: a pack nobody has copied yet shows no counter at all.
  if (!(copies > 0)) {
    // Drop the counter leaf, then any wrapper whose only text was that counter
    // (icons and spacing included), so no orphaned flame or label remains.
    markup = markup.replace(COPIES_COUNTER_ELEMENT_PATTERN, '<!--copies-->')
    let previous
    do {
      previous = markup
      markup = markup.replace(COPIES_WRAPPER_PATTERN, '<!--copies-->')
    } while (markup !== previous)
    markup = markup.replace(COPIES_COMMENT_PATTERN, '')
  }
  const tokens = {
    TITLE: pack.title || 'Untitled prepack',
    SUBTITLE: subtitle,
    TYPE: type,
    BET_TYPE: type,
    TAGS: tags.join(', '),
    COMBINED_ODDS: displayOdds(combinedOdds, pack.payout_multiplier ?? pack.payoutMultiplier, oddsFormat),
    LEG_COUNT: legs.length,
    COPIES: copies > 0 ? copies.toLocaleString('en-US') : '',
    // Words that follow a count, so "1 bets" never ships.
    COPIES_LABEL: copies === 1 ? 'bet placed' : 'bets placed',
    LEG_COUNT_LABEL: legs.length === 1 ? 'leg' : 'legs',
    SUMMARY: pack.summary || '',
    EVENT: event === '—' ? '' : event,
    EXAMPLE_PAYOUT: examplePayoutLabel({ payout_multiplier: pack.payout_multiplier ?? pack.payoutMultiplier, odds: combinedOdds }, exampleStake, { currency, locale }),
  }
  for (let tIndex = 0; tIndex < 5; tIndex += 1) {
    tokens[`TAG_${tIndex + 1}`] = tags[tIndex] || ''
  }
  for (let index = 0; index < 6; index += 1) {
    const leg = index < visibleLegCount ? legs[index] : undefined
    const participant = leg?.player || leg?.participant
    const selection = leg?.selection_name || leg?.selectionName
    const market = leg?.market_label || leg?.marketLabel || leg?.market_name
    const legEvent = leg?.event_label || leg?.eventLabel || ''
    if (legEvent && legNeedsEvent(leg)) {
      tokens[`LEG_${index + 1}_PLAYER`] = legEvent
      tokens[`LEG_${index + 1}_BET`] = sideBetLabel(selection || '', market || '') || leg.bet || ''
      tokens[`LEG_${index + 1}_EVENT`] = ''
    } else {
      tokens[`LEG_${index + 1}_PLAYER`] = participant || selection || ''
      tokens[`LEG_${index + 1}_BET`] = legBetLabel(leg, oddsFormat)
      tokens[`LEG_${index + 1}_EVENT`] = legEvent
    }
    tokens[`LEG_${index + 1}_ODDS`] = displayOdds(leg?.odds || leg?.american_odds, leg?.decimal_odds ?? leg?.decimalOdds, oddsFormat)
    tokens[`LEG_${index + 1}_FORM`] = leg ? legForms[index] || '' : ''
    tokens[`LEG_${index + 1}_GLYPH`] = leg ? sportGlyph(leg.sport || pack.sport, leg.league || pack.league || '') : ''
    const team = legTeamTokens(leg, pack.sport)
    tokens[`LEG_${index + 1}_LOGO`] = team.logo
    tokens[`LEG_${index + 1}_JERSEY`] = team.jersey
    tokens[`LEG_${index + 1}_JERSEY_PLAIN`] = team.plainJersey
    tokens[`LEG_${index + 1}_TEAM`] = team.team
    tokens[`LEG_${index + 1}_MARK`] = team.mark
  }
  return markup.replace(TEMPLATE_TOKEN_PATTERN, (match, key) => (Object.hasOwn(tokens, key) ? escapeHtml(tokens[key]) : match))
}

// A pack's variable value goes into the stylesheet as written, so one that
// could close the declaration or the style block is not applied at all.
export function safeCssValue(value) {
  return typeof value === 'string' && value.length <= 16384 && !CSS_UNSAFE_CHARACTER_PATTERN.test(value) ? value : undefined
}

// A pack whose picture the operator removed (#606) carries banner_hidden instead of a stored `none`, which older
// validation rejects; feed-api sends the widget `none`, and Studio previews translate it here the same way.
function bannerOverride(varOverrides, key) {
  const value = safeCssValue(varOverrides?.[key])
  return value === undefined && key === '--banner-image' && varOverrides?.banner_hidden === true ? 'none' : value
}

/**
 * A template's variables in `colorMode` as [key, value] pairs, each with a pack's own value as the artwork takes it
 * (bannerOverride). The widget's card shell reads them too, as it draws a whole-card picture itself (#1038).
 */
export function artworkVariables(template, colorMode, varOverrides) {
  return getTemplateVariant(template, colorMode).cssVars.map((variable) => [variable.key, String(bannerOverride(varOverrides, variable.key) ?? variable.value)])
}

const CARD_VAR = /^var\(\s*(--[\w-]+)\s*(?:,\s*(.*))?\)$/s
const MAX_CARD_VAR_HOPS = 5
/**
 * What a card value comes to when its `var(--name, fallback)` references are followed through the card's own
 * variables (`vars`, as the card article sets them), falling back as CSS does. A value that is not a lone var() is
 * returned trimmed; undefined for a non-string, an unset variable without a fallback, or past five hops.
 */
export function resolveCardVar(value, vars) {
  let current = value
  for (let hop = 0; hop <= MAX_CARD_VAR_HOPS; hop += 1) {
    if (typeof current !== 'string') return undefined
    const text = current.trim()
    const match = CARD_VAR.exec(text)
    if (!match) return text
    if (hop === MAX_CARD_VAR_HOPS) return undefined
    current = vars?.[match[1]] ?? match[2]
  }
  return undefined
}

const COLOR_SCHEME = /color-scheme\s*:\s*(light|dark)\b/g
/**
 * The color scheme a template's artwork declares in `colorMode` (the last light or dark `color-scheme` it sets
 * outside a comment), or undefined. A frame of another scheme than its document paints an opaque canvas, so a frame wearing the same one
 * stays see-through, which artwork showing the card's own picture needs (#1038); a frame whose artwork declares none
 * is left as it was.
 */
export function artworkColorScheme(template, colorMode) {
  let scheme
  for (const match of withoutCssComments((template?.cssCode || '') + (getTemplateVariant(template, colorMode).cssCode || '')).matchAll(COLOR_SCHEME)) scheme = match[1]
  return scheme
}

export function buildSrcDoc(template, varOverrides, scale, pack, { center = false, colorMode = 'dark', publicCard = false, exampleStake = 10, visibleLegCount = Infinity, oddsFormat = 'american', currency = 'USD', locale = 'en-US', legForms = [] } = {}) {
  const variant = getTemplateVariant(template, colorMode)
  const variables = artworkVariables(template, colorMode, varOverrides)
    .map(([key, value]) => `  ${key}: ${value};`)
    .join('\n')
  const layoutCss = scale && scale !== 1
    ? `body{display:flex;justify-content:center;transform:scale(${scale});transform-origin:top left;width:${Math.round(100 / scale)}%;}`
    : center
      ? 'body{display:flex;min-height:100vh;align-items:center;justify-content:center;}'
      : ''
  const markup = pack ? injectPackTokens(template?.htmlCode || '', pack, { exampleStake, visibleLegCount, oddsFormat, currency, locale, legForms }) : (template?.htmlCode || '')
  const resolvedCss = resolveBuiltinCssUrls((template?.cssCode || '') + variant.cssCode)
  const builtin = builtinById.get(template?.id)
  // Sanitization removes sample controls, not authorship. Only untouched
  // builtin artwork delegates its frame to the native article; retained IDs
  // with authored HTML, shared CSS or active-mode CSS keep their own framing.
  const compositionCss = publicCard
    && hasCanonicalArtwork(template, builtin)
    && template.cssCode === builtin.cssCode
    && variant.cssCode === builtin.variants[colorMode === 'light' ? 'light' : 'dark'].cssCode
    ? 'html,body{min-height:0;background:transparent}body>.card{border:0;border-radius:0;box-shadow:none}body>.card>.panel{border:0;border-radius:0;background:var(--bg)}body>.card:has(>.panel){padding:0}body>.card>.heading{padding:12px 16px 8px;background:var(--gradient);color:#fff}.footer:empty,.options:empty{display:none}'
    : ''
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src 'self' data: https: http://localhost:* http://127.0.0.1:*; style-src 'unsafe-inline'; font-src 'self' https:; base-uri 'none'; form-action 'none'"><style>${LEG_LAYOUT_CSS}:root{\n${variables}\n}${resolvedCss}${compositionCss}${layoutCss}</style></head><body>${markup}</body></html>`
}

export function sanitizePublicTemplate(template) {
  if (!template || !template.htmlCode) return template
  if (typeof DOMParser === 'undefined') {
    return publicArtwork(template, sanitizeWithoutDOM(template.htmlCode))
  }
  const doc = new DOMParser().parseFromString(template.htmlCode, 'text/html')
  for (const control of doc.querySelectorAll('.cta, .edit, .details, button, input, select, textarea, [role="button"]')) {
    control.remove()
  }
  for (const link of doc.querySelectorAll('a[href]')) link.removeAttribute('href')
  for (const element of doc.querySelectorAll('.option, .odds, .combined')) {
    if (element.textContent.includes('{{COMBINED_ODDS}}')) element.remove()
  }
  for (const element of doc.querySelectorAll('*')) {
    if (!element.childElementCount) {
      const label = element.textContent.trim()
      if (
        label.includes('{{COMBINED_ODDS}}')
        || ['Edit', 'View details', 'Tail it!', 'Lock it in', 'Add to slip', 'Lock it in »'].includes(label)
      ) {
        element.remove()
      }
      if (label === 'Bet boost »' || label === 'Bet boost') {
        element.textContent = '{{TYPE}}'
      }
    }
  }
  const walker = doc.createTreeWalker(doc.body, 4)
  while (walker.nextNode()) {
    walker.currentNode.textContent = walker.currentNode.textContent.replace(COMBINED_ODDS_TOKENS_PATTERN, '')
  }
  for (const element of doc.querySelectorAll('.footer, .options')) {
    if (!element.textContent.trim() && !element.querySelector('img,svg')) element.remove()
  }
  return publicArtwork(template, doc.body.innerHTML)
}

// SSR has no DOMParser. Track nested element ranges instead of a regex that
// stops at the first closing tag and leaves fragments of an authored control.
function sanitizeWithoutDOM(html) {
  const stack = []
  const removals = []
  const voidTags = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'])
  for (const match of html.matchAll(HTML_TAG_OR_TEXT_PATTERN)) {
    const token = match[0]
    if (!token.startsWith('<')) {
      if (COMBINED_ODDS_TOKEN_PATTERN.test(token) || DEFAULT_BET_BUTTON_LABEL_PATTERN.test(token.trim())) {
        const container = [...stack].reverse().find((node) => node.quote)
        if (container) container.remove = true
        else removals.push([match.index, match.index + token.length])
      }
      continue
    }
    const tag = match[1].toLowerCase()
    if (token.startsWith('</')) {
      const index = stack.map((node) => node.tag).lastIndexOf(tag)
      if (index < 0) continue
      for (const node of stack.splice(index)) {
        if (node.remove) removals.push([node.start, match.index + token.length])
      }
      continue
    }
    const classes = token.match(HTML_CLASS_ATTRIBUTE_PATTERN)?.[1].split(WHITESPACE_PATTERN) || []
    const remove = ['button', 'input', 'select', 'textarea'].includes(tag)
      || classes.some((name) => ['cta', 'edit', 'details'].includes(name))
      || BUTTON_ROLE_ATTRIBUTE_PATTERN.test(token)
    const node = { tag, start: match.index, remove, quote: classes.some((name) => ['option', 'odds', 'combined'].includes(name)) }
    if (voidTags.has(tag) || token.endsWith('/>')) {
      if (remove) removals.push([match.index, match.index + token.length])
    } else stack.push(node)
  }
  for (const node of stack) if (node.remove) removals.push([node.start, html.length])
  removals.sort((a, b) => a[0] - b[0])
  let output = ''
  let end = 0
  for (const [start, next] of removals) {
    if (start >= end) output += html.slice(end, start)
    end = Math.max(end, next)
  }
  return (output + html.slice(end))
    .replace(HTML_ANCHOR_PATTERN, (anchor) =>
      anchor.replace(HTML_HREF_ATTRIBUTE_PATTERN, ''))
    .replace(BET_BOOST_LABEL_PATTERN, '{{TYPE}}')
    .replace(EMPTY_TEMPLATE_FOOTER_PATTERN, '')
}
